/**
 * useDocumentRecord — the open document's server-side name and folder (see
 * ``documentIdentity``). Refetches whenever ``refreshKey`` changes (a run
 * starts or loads, or the writer comes back from Home / Drafts, where
 * renaming and refiling happen). Failures keep the last record.
 */

import { useEffect, useState } from "react";
import { listActiveRuns, listRuns } from "../../api/client";
import { type DocumentRecord, documentRecordFromRuns } from "../documentIdentity";

export function useDocumentRecord(docId: string | null, refreshKey: string): DocumentRecord | null {
  const [record, setRecord] = useState<DocumentRecord | null>(null);
  useEffect(() => {
    void refreshKey; // refetch trigger
    if (!docId) {
      setRecord(null);
      return;
    }
    let gone = false;
    Promise.all([listRuns({ docId, limit: 10 }), listActiveRuns({ docId, limit: 5 })])
      .then(([done, live]) => {
        if (gone) return;
        setRecord(
          documentRecordFromRuns(
            done.documents.flatMap((g) => g.runs),
            live.runs ?? [],
          ),
        );
      })
      .catch((err: unknown) => {
        console.warn("[useDocumentRecord] run lookup failed:", err);
      });
    return () => {
      gone = true;
    };
  }, [docId, refreshKey]);
  return record;
}
